import type {
  Plugin,
  EntityPluginContext,
  EntityTypeConfig,
  JobHandler,
  Template,
  CreateInput,
  CreateExecutionContext,
  CreateInterceptionResult,
} from "@brains/plugins";
import { EntityPlugin } from "@brains/plugins";
import { AtprotoProjectionRegistry } from "@brains/atproto-contracts";
import { z } from "@brains/utils/zod";
import { noteSchema, type Note } from "./schemas/note";
import { noteAdapter, type NoteAdapter } from "./adapters/note-adapter";
import type { NoteConfig, NoteConfigInput } from "./config";
import { noteConfigSchema } from "./config";
import { noteGenerationTemplate } from "./templates/generation-template";
import { NoteGenerationJobHandler } from "./handlers/noteGenerationJobHandler";
import { UploadMarkdownImportJobHandler } from "./handlers/uploadMarkdownImportJobHandler";
import {
  getMarkdownImportIdentity,
  isSupportedMarkdownUploadMediaType,
  readTextUploadMarkdown,
} from "./lib/upload-markdown-import";
import { createNoteAtprotoProjection } from "./atproto-projection";
import packageJson from "../package.json";
import { getErrorMessage } from "@brains/utils/error";
import {
  NOTE_CAPTURE_MESSAGE,
  noteCaptureRequestSchema,
  type NoteCaptureResponse,
} from "@brains/contracts";

const webChatUploadsScope = {
  namespace: "upload",
  refKind: "upload",
  routePath: "/api/chat/uploads",
} as const;

interface GenerateNoteEvalInput {
  prompt: string;
}

const generateNoteEvalInputSchema: z.ZodType<GenerateNoteEvalInput> = z.object({
  prompt: z.string(),
});

export class NotePlugin extends EntityPlugin<
  Note,
  NoteConfig,
  NoteConfigInput
> {
  readonly entityType: typeof noteAdapter.entityType = noteAdapter.entityType;
  readonly schema: typeof noteSchema = noteSchema;
  readonly adapter: NoteAdapter = noteAdapter;
  private unregisterAtprotoProjection: (() => void) | undefined;

  constructor(config: NoteConfigInput = {}) {
    super("note", packageJson, config, noteConfigSchema);
  }

  protected override getEntityTypeConfig(): EntityTypeConfig | undefined {
    return { projectionSourceRole: "primary", markdownImport: true };
  }

  protected override async interceptCreate(
    input: CreateInput,
    _executionContext: CreateExecutionContext,
    context: EntityPluginContext,
  ): Promise<CreateInterceptionResult> {
    if (input.from?.kind !== webChatUploadsScope.refKind) {
      return { kind: "continue", input };
    }

    if (input.transform !== "extract-markdown") {
      return {
        kind: "handled",
        result: {
          success: false,
          error:
            'Importing an upload as a note requires transform: "extract-markdown"',
        },
      };
    }

    const uploadId = input.from.id;
    let uploadRecord;
    try {
      uploadRecord = await context.uploads
        .scoped(webChatUploadsScope)
        .readRecord(uploadId);
    } catch {
      return {
        kind: "handled",
        result: { success: false, error: "Upload ref not found" },
      };
    }

    try {
      if (!isSupportedMarkdownUploadMediaType(uploadRecord.mediaType)) {
        return {
          kind: "handled",
          result: {
            success: false,
            error:
              "Only text, JSON, and PDF uploads can be imported as markdown notes",
          },
        };
      }

      // Without a given title, a text upload is named by its first heading.
      const markdown =
        input.title === undefined
          ? readTextUploadMarkdown(
              await context.uploads.scoped(webChatUploadsScope).read(uploadId),
            )
          : undefined;
      const identity = getMarkdownImportIdentity({
        filename: uploadRecord.filename,
        title: input.title,
        markdown,
      });
      // Create the stub before enqueueing so the returned entityId is the
      // real (possibly deduplicated) id and exists while "generating".
      const stub = noteAdapter.buildStub({
        id: identity.id,
        title: identity.title,
      });
      const now = new Date().toISOString();
      const created = await context.entityService.createEntity({
        entity: {
          id: identity.id,
          entityType: "note",
          content: stub.content,
          metadata: stub.metadata,
          ...(input.visibility !== undefined
            ? { visibility: input.visibility }
            : {}),
          created: now,
          updated: now,
        },
        options: { deduplicateId: true },
      });
      const stubEntity = await context.entityService.getEntity({
        entityType: "note",
        id: created.entityId,
        visibilityScope: "restricted",
      });
      if (!stubEntity) {
        throw new Error("Created note import stub could not be read");
      }
      const jobId = await context.jobs.enqueue({
        type: "upload-import",
        data: {
          uploadId,
          entityId: created.entityId,
          stubContentHash: stubEntity.contentHash,
          ...(input.title !== undefined ? { title: input.title } : {}),
        },
      });

      return {
        kind: "handled",
        result: {
          success: true,
          data: { entityId: created.entityId, status: "generating", jobId },
        },
      };
    } catch (error) {
      return {
        kind: "handled",
        result: {
          success: false,
          error: getErrorMessage(error, "Failed to import upload as markdown"),
        },
      };
    }
  }

  protected override createGenerationHandler(
    context: EntityPluginContext,
  ): JobHandler | null {
    return new NoteGenerationJobHandler(
      this.logger.child("NoteGenerationJobHandler"),
      context,
    );
  }

  protected override getTemplates(): Record<string, Template> {
    return { generation: noteGenerationTemplate };
  }

  protected override async onRegister(
    context: EntityPluginContext,
  ): Promise<void> {
    context.jobs.registerHandler(
      "upload-import",
      new UploadMarkdownImportJobHandler(
        this.logger.child("UploadMarkdownImportJobHandler"),
        context,
      ),
    );

    context.eval.registerHandler("generateNote", async (input: unknown) => {
      const parsed: GenerateNoteEvalInput =
        generateNoteEvalInputSchema.parse(input);
      return context.ai.generate(
        {
          prompt: parsed.prompt,
          templateName: "note:generation",
          representedIdentity: "none",
        },
        z.object({ title: z.string(), body: z.string() }),
      );
    });

    // Another plugin keeps text as a note; always private to the owner.
    context.messaging.subscribe<unknown, NoteCaptureResponse>(
      NOTE_CAPTURE_MESSAGE,
      async (message) => {
        const request = noteCaptureRequestSchema.safeParse(message.payload);
        if (!request.success)
          return { success: false, error: "Invalid note capture request" };
        const { id, title, body } = request.data;
        const existing = await context.entityService.getEntity({
          entityType: "note",
          id,
          visibilityScope: "restricted",
        });
        if (existing)
          return { success: true, data: { noteId: id, created: false } };
        await context.entityService.createEntityFromMarkdown({
          input: {
            entityType: "note",
            id,
            markdown: `---\ntitle: ${JSON.stringify(title)}\n---\n\n${body}\n`,
            visibility: "restricted",
          },
          options: { conditionalWrite: { expectedRevision: null } },
        });
        return { success: true, data: { noteId: id, created: true } };
      },
    );

    this.unregisterAtprotoProjection =
      AtprotoProjectionRegistry.getInstance().register(
        createNoteAtprotoProjection(),
      );
  }

  protected override async getInstructions(): Promise<string> {
    return "Note entities are editable markdown notes. Use them for durable text captures, summaries, and imported markdown; raw files remain document or image entities instead of notes.";
  }

  protected override async onShutdown(): Promise<void> {
    this.unregisterAtprotoProjection?.();
    this.unregisterAtprotoProjection = undefined;
  }
}

export function notePlugin(config: NoteConfigInput = {}): Plugin {
  return new NotePlugin(config);
}
